import { format } from 'date-fns';
import { FilePen, Globe, GlobeLock, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../components/Badge';
import { Modal } from '../components/Modal';
import { Tabs } from '../components/Tabs';
import { BlogPostForm } from '../components/forms/BlogPostForm';
import {
  BlogTaxonomyModal,
  type TaxonomyEntity,
  type TaxonomyFormValues,
} from '../components/forms/BlogTaxonomyModal';
import { ConfirmDialog } from '../components/forms/ConfirmDialog';
import {
  type BlogCategory,
  type BlogChannel,
  type BlogPostListItem,
  type CreateBlogPostPayload,
  useCategories,
  useChannels,
  useCreateCategory,
  useCreateChannel,
  useCreatePost,
  useDeletePost,
  usePost,
  usePosts,
  usePublishPost,
  useUnpublishPost,
  useUpdateCategory,
  useUpdateChannel,
  useUpdatePost,
} from '../hooks/useBlogData';

const PAGE_SIZE = 50;

type Notification = { type: 'success' | 'error'; message: string } | null;

function statusBadgeVariant(status: string): 'success' | 'warning' {
  return status === 'PUBLISHED' ? 'success' : 'warning';
}

function PostsTab({ notify }: { notify: (type: 'success' | 'error', message: string) => void }) {
  const [page, setPage] = useState(1);
  const offset = (page - 1) * PAGE_SIZE;

  const [createOpen, setCreateOpen] = useState(false);
  const [editTargetId, setEditTargetId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BlogPostListItem | null>(null);

  const { data, isLoading, isError } = usePosts({ limit: PAGE_SIZE, page });
  const { data: channels = [] } = useChannels();
  const { data: categories = [] } = useCategories();
  const editQuery = usePost(editTargetId ?? '');

  const createMutation = useCreatePost();
  const updateMutation = useUpdatePost();
  const publishMutation = usePublishPost();
  const unpublishMutation = useUnpublishPost();
  const deleteMutation = useDeletePost();

  const posts = data?.data ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  function handleCreate(payload: CreateBlogPostPayload, publish: boolean) {
    createMutation.mutate(payload, {
      onSuccess: (created) => {
        if (publish) {
          publishMutation.mutate(created.id, {
            onSuccess: () => {
              setCreateOpen(false);
              notify('success', 'Post published');
            },
            onError: () => notify('error', 'Post saved but failed to publish'),
          });
        } else {
          setCreateOpen(false);
          notify('success', 'Draft saved');
        }
      },
      onError: () => notify('error', 'Failed to save post'),
    });
  }

  function handleUpdate(payload: CreateBlogPostPayload, publish: boolean) {
    if (!editTargetId) return;
    const id = editTargetId;
    updateMutation.mutate(
      { id, data: payload },
      {
        onSuccess: () => {
          if (publish) {
            publishMutation.mutate(id, {
              onSuccess: () => {
                setEditTargetId(null);
                notify('success', 'Post published');
              },
              onError: () => notify('error', 'Failed to publish post'),
            });
          } else {
            setEditTargetId(null);
            notify('success', 'Changes saved');
          }
        },
        onError: () => notify('error', 'Failed to update post'),
      },
    );
  }

  function handlePublish(post: BlogPostListItem) {
    publishMutation.mutate(post.id, {
      onSuccess: () => notify('success', 'Post published'),
      onError: () => notify('error', 'Failed to publish'),
    });
  }

  function handleUnpublish(post: BlogPostListItem) {
    unpublishMutation.mutate(post.id, {
      onSuccess: () => notify('success', 'Post unpublished'),
      onError: () => notify('error', 'Failed to unpublish'),
    });
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        notify('success', 'Post deleted');
      },
      onError: () => notify('error', 'Failed to delete post'),
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors"
        >
          <Plus size={16} />
          New post
        </button>
      </div>

      {isLoading ? (
        <div className="bg-surface rounded-xl border border-border p-4 animate-pulse space-y-2">
          <div className="h-4 bg-border rounded w-full" />
          <div className="h-4 bg-border rounded w-3/4" />
          <div className="h-4 bg-border rounded w-5/6" />
        </div>
      ) : isError ? (
        <div className="bg-surface rounded-xl border border-border px-5 py-8 text-center text-sm">
          <p className="text-danger font-medium">Failed to load posts</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-3 text-text-secondary underline hover:text-text transition-colors text-xs"
          >
            Retry
          </button>
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-surface rounded-xl border border-border px-5 py-8 text-center text-text-muted text-sm">
          No posts found
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-muted">
                  <th className="px-5 py-2.5 font-medium text-xs">Title</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Status</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Channels</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Categories</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Updated</th>
                  <th className="px-5 py-2.5 font-medium text-xs" />
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr
                    key={post.id}
                    className="border-b border-border last:border-b-0 hover:bg-surface-hover transition-colors"
                  >
                    <td className="px-5 py-2.5">
                      <span className="text-text font-medium">{post.title}</span>
                    </td>
                    <td className="px-5 py-2.5">
                      <Badge variant={statusBadgeVariant(post.status)}>{post.status}</Badge>
                    </td>
                    <td className="px-5 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {post.channels.length > 0 ? (
                          post.channels.map((c) => (
                            <Badge key={c.id} variant="info">
                              {c.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-text-muted">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {post.categories.length > 0 ? (
                          post.categories.map((c) => (
                            <Badge key={c.id} variant="default">
                              {c.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-text-muted">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary">
                        {format(new Date(post.updatedAt), 'MMM d, yyyy')}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          type="button"
                          onClick={() => setEditTargetId(post.id)}
                          className="p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
                          title="Edit"
                          aria-label="Edit"
                        >
                          <FilePen size={15} />
                        </button>
                        {post.status === 'DRAFT' ? (
                          <button
                            type="button"
                            onClick={() => handlePublish(post)}
                            disabled={publishMutation.isPending}
                            className="p-1.5 rounded-md text-text-muted hover:text-success hover:bg-success/10 transition-colors disabled:opacity-40"
                            title="Publish"
                            aria-label="Publish"
                          >
                            <Globe size={15} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleUnpublish(post)}
                            disabled={unpublishMutation.isPending}
                            className="p-1.5 rounded-md text-text-muted hover:text-warning hover:bg-warning/10 transition-colors disabled:opacity-40"
                            title="Unpublish"
                            aria-label="Unpublish"
                          >
                            <GlobeLock size={15} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(post)}
                          className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-danger/10 transition-colors"
                          title="Delete"
                          aria-label="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-5 py-3 text-sm text-text-secondary border-t border-border">
            <span>
              {(data?.total ?? 0) > 0
                ? `${offset + 1}-${Math.min(offset + PAGE_SIZE, data?.total ?? 0)} of ${data?.total}`
                : '0 results'}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded-md hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded-md hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Create post" size="xl">
        <BlogPostForm
          channels={channels}
          categories={categories}
          onSaveDraft={(p) => handleCreate(p, false)}
          onSavePublish={(p) => handleCreate(p, true)}
          isPending={createMutation.isPending || publishMutation.isPending}
          onClose={() => setCreateOpen(false)}
        />
      </Modal>

      {editTargetId && (
        <Modal open onClose={() => setEditTargetId(null)} title="Edit post" size="xl">
          {editQuery.isLoading || !editQuery.data ? (
            <div className="py-8 text-center text-sm text-text-muted">Loading post...</div>
          ) : (
            <BlogPostForm
              initial={editQuery.data}
              channels={channels}
              categories={categories}
              onSaveDraft={(p) => handleUpdate(p, false)}
              onSavePublish={(p) => handleUpdate(p, true)}
              isPending={updateMutation.isPending || publishMutation.isPending}
              onClose={() => setEditTargetId(null)}
            />
          )}
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          open
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Confirm delete"
          message={`Permanently delete "${deleteTarget.title}"? This cannot be undone.`}
          confirmLabel="Delete"
          confirmVariant="danger"
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

function ChannelsTab({ notify }: { notify: (type: 'success' | 'error', message: string) => void }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<BlogChannel | null>(null);

  const { data: channels = [], isLoading, isError } = useChannels();
  const createMutation = useCreateChannel();
  const updateMutation = useUpdateChannel();

  function handleSubmit(values: TaxonomyFormValues) {
    if (editTarget) {
      updateMutation.mutate(
        { id: editTarget.id, data: values },
        {
          onSuccess: () => {
            setEditTarget(null);
            notify('success', 'Channel updated');
          },
          onError: () => notify('error', 'Failed to update channel'),
        },
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          setCreateOpen(false);
          notify('success', 'Channel created');
        },
        onError: () => notify('error', 'Failed to create channel'),
      });
    }
  }

  return (
    <TaxonomyTable
      entityLabel="channel"
      isLoading={isLoading}
      isError={isError}
      rows={channels}
      renderExtraColumn={{
        header: 'Posts',
        render: (row) => (channels.find((c) => c.id === row.id)?.postCount ?? 0).toString(),
      }}
      onCreate={() => setCreateOpen(true)}
      onEdit={(row) => setEditTarget(channels.find((c) => c.id === row.id) ?? null)}
      createOpen={createOpen}
      onCreateClose={() => setCreateOpen(false)}
      editTarget={editTarget}
      onEditClose={() => setEditTarget(null)}
      onSubmit={handleSubmit}
      isPending={createMutation.isPending || updateMutation.isPending}
    />
  );
}

function CategoriesTab({
  notify,
}: {
  notify: (type: 'success' | 'error', message: string) => void;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<BlogCategory | null>(null);

  const { data: categories = [], isLoading, isError } = useCategories();
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();

  function handleSubmit(values: TaxonomyFormValues) {
    if (editTarget) {
      updateMutation.mutate(
        { id: editTarget.id, data: values },
        {
          onSuccess: () => {
            setEditTarget(null);
            notify('success', 'Category updated');
          },
          onError: () => notify('error', 'Failed to update category'),
        },
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          setCreateOpen(false);
          notify('success', 'Category created');
        },
        onError: () => notify('error', 'Failed to create category'),
      });
    }
  }

  return (
    <TaxonomyTable
      entityLabel="category"
      isLoading={isLoading}
      isError={isError}
      rows={categories}
      onCreate={() => setCreateOpen(true)}
      onEdit={(row) => setEditTarget(categories.find((c) => c.id === row.id) ?? null)}
      createOpen={createOpen}
      onCreateClose={() => setCreateOpen(false)}
      editTarget={editTarget}
      onEditClose={() => setEditTarget(null)}
      onSubmit={handleSubmit}
      isPending={createMutation.isPending || updateMutation.isPending}
    />
  );
}

interface TaxonomyTableProps {
  entityLabel: string;
  isLoading: boolean;
  isError: boolean;
  rows: TaxonomyEntity[];
  renderExtraColumn?: { header: string; render: (row: TaxonomyEntity) => string };
  onCreate: () => void;
  onEdit: (row: TaxonomyEntity) => void;
  createOpen: boolean;
  onCreateClose: () => void;
  editTarget: TaxonomyEntity | null;
  onEditClose: () => void;
  onSubmit: (values: TaxonomyFormValues) => void;
  isPending: boolean;
}

function TaxonomyTable({
  entityLabel,
  isLoading,
  isError,
  rows,
  renderExtraColumn,
  onCreate,
  onEdit,
  createOpen,
  onCreateClose,
  editTarget,
  onEditClose,
  onSubmit,
  isPending,
}: TaxonomyTableProps) {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={onCreate}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors"
        >
          <Plus size={16} />
          New {entityLabel}
        </button>
      </div>

      {isLoading ? (
        <div className="bg-surface rounded-xl border border-border p-4 animate-pulse space-y-2">
          <div className="h-4 bg-border rounded w-full" />
          <div className="h-4 bg-border rounded w-3/4" />
        </div>
      ) : isError ? (
        <div className="bg-surface rounded-xl border border-border px-5 py-8 text-center text-sm">
          <p className="text-danger font-medium">Failed to load {entityLabel}s</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-surface rounded-xl border border-border px-5 py-8 text-center text-text-muted text-sm">
          No {entityLabel}s found
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-muted">
                  <th className="px-5 py-2.5 font-medium text-xs">Name</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Key</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Description</th>
                  {renderExtraColumn && (
                    <th className="px-5 py-2.5 font-medium text-xs">{renderExtraColumn.header}</th>
                  )}
                  <th className="px-5 py-2.5 font-medium text-xs">Status</th>
                  <th className="px-5 py-2.5 font-medium text-xs" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-border last:border-b-0 hover:bg-surface-hover transition-colors"
                  >
                    <td className="px-5 py-2.5">
                      <span className="text-text font-medium">{row.name}</span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary font-mono text-xs">{row.key}</span>
                    </td>
                    <td className="px-5 py-2.5">
                      {row.description ? (
                        <span className="text-text-secondary">{row.description}</span>
                      ) : (
                        <span className="text-text-muted">-</span>
                      )}
                    </td>
                    {renderExtraColumn && (
                      <td className="px-5 py-2.5">
                        <span className="text-text-secondary">{renderExtraColumn.render(row)}</span>
                      </td>
                    )}
                    <td className="px-5 py-2.5">
                      <Badge variant={row.isActive ? 'success' : 'default'}>
                        {row.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center justify-end">
                        <button
                          type="button"
                          onClick={() => onEdit(row)}
                          className="p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
                          title="Edit"
                          aria-label="Edit"
                        >
                          <FilePen size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <BlogTaxonomyModal
        open={createOpen}
        onClose={onCreateClose}
        onSubmit={onSubmit}
        entityLabel={entityLabel}
        isPending={isPending}
      />

      {editTarget && (
        <BlogTaxonomyModal
          open
          onClose={onEditClose}
          onSubmit={onSubmit}
          initial={editTarget}
          entityLabel={entityLabel}
          isPending={isPending}
        />
      )}
    </div>
  );
}

export function Blog() {
  const [notification, setNotification] = useState<Notification>(null);

  function notify(type: 'success' | 'error', message: string) {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Blog</h1>
        <p className="text-sm text-text-muted mt-0.5">
          Author multichannel blog posts and manage channels and categories.
        </p>
      </div>

      <Tabs
        tabs={[
          { key: 'posts', label: 'Posts', content: <PostsTab notify={notify} /> },
          { key: 'channels', label: 'Channels', content: <ChannelsTab notify={notify} /> },
          { key: 'categories', label: 'Categories', content: <CategoriesTab notify={notify} /> },
        ]}
      />

      {notification && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg border text-sm shadow-lg z-50 ${
            notification.type === 'success'
              ? 'bg-success/15 border-success/30 text-success'
              : 'bg-danger/15 border-danger/30 text-danger'
          }`}
        >
          {notification.message}
        </div>
      )}
    </div>
  );
}
